import React, { useEffect, useState } from "react";
import { Shell, Section, KpiCard } from "@/components/loomi/Layout";
import { ADMIN_NAV } from "./AdminDashboard";
import { api, formatApiError, copyToClipboard } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Key, RefreshCw, X, Link2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { CopyField } from "@/components/loomi/CopyField";

export default function AdminVendors() {
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [issued, setIssued] = useState(null); // {api_key, webhook_secret} shown once

  const load = async () => { const { data } = await api.get("/admin/vendors"); setVendors(data); };
  useEffect(() => { load(); }, []);

  const rotate = async (vid, name) => {
    if (!window.confirm(`Rotate API key for "${name}"? The old key will stop working immediately.`)) return;
    try {
      const { data } = await api.post(`/admin/vendors/${vid}/rotate-key`);
      setIssued({ ...data, vendor_name: name, mode: "rotated" });
      load();
    } catch (e) { toast.error("Rotate failed", { description: formatApiError(e) }); }
  };

  const toggleActive = async (v) => {
    try {
      await api.patch(`/admin/vendors/${v.id}`, { active: !v.active });
      load();
    } catch (e) { toast.error("Failed", { description: formatApiError(e) }); }
  };

  return (
    <Shell title="Vendor API integrations" portalLabel="Admin" nav={ADMIN_NAV}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Vendors" value={vendors.length} accent="primary" testId="vendors-kpi-count" />
        <KpiCard label="Active" value={vendors.filter(v => v.active).length} accent="green" testId="vendors-kpi-active" />
        <KpiCard label="Total starts" value={vendors.reduce((s,v) => s + (v.counters?.starts || 0), 0)} accent="blue" testId="vendors-kpi-starts" />
        <KpiCard label="Total completes" value={vendors.reduce((s,v) => s + (v.counters?.complete || 0), 0)} accent="green" testId="vendors-kpi-completes" />
      </div>

      <div className="mt-8">
        <Section title="Vendors" testId="section-vendors" action={
          <div className="flex gap-2">
            <Link to="/docs/vendor" target="_blank" className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-md px-3 py-2 inline-flex items-center gap-1.5" data-testid="vendors-view-docs">
              <ExternalLink className="w-3.5 h-3.5" /> API docs
            </Link>
            <button onClick={() => setOpen(true)} data-testid="vendors-new-btn" className="bg-indigo-500 hover:bg-indigo-400 text-xs font-medium px-3 py-2 rounded-md inline-flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> New vendor
            </button>
          </div>
        }>
          {vendors.length === 0 ? (
            <div className="text-mute text-sm py-8 text-center" data-testid="vendors-empty">No vendors yet. Create one to enable API integrations.</div>
          ) : (
            <table className="w-full text-sm" data-testid="vendors-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-mute border-b border-white/10">
                  <th className="py-3 pr-4">Vendor</th><th className="py-3 pr-4">API key</th><th className="py-3 pr-4">Postback URL</th>
                  <th className="py-3 pr-4 text-right">Starts</th><th className="py-3 pr-4 text-right">Completes</th>
                  <th className="py-3 pr-4">Active</th><th className="py-3" />
                </tr>
              </thead>
              <tbody>
                {vendors.map((v) => (
                  <tr key={v.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`vendor-row-${v.id}`}>
                    <td className="py-3 pr-4">
                      <div className="font-medium">{v.name}</div>
                      <div className="text-[10px] text-mute font-mono">{v.contact_email || "—"}</div>
                    </td>
                    <td className="py-3 pr-4 font-mono text-xs">loomi_sk_…{v.api_key_last4}</td>
                    <td className="py-3 pr-4 font-mono text-[11px] text-mute max-w-[240px] truncate">{v.postback_url || <span className="italic">none</span>}</td>
                    <td className="py-3 pr-4 text-right font-mono">{v.counters?.starts || 0}</td>
                    <td className="py-3 pr-4 text-right font-mono status-complete">{v.counters?.complete || 0}</td>
                    <td className="py-3 pr-4"><button onClick={() => toggleActive(v)} data-testid={`vendor-toggle-${v.id}`}
                      className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${v.active ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-white/5 text-mute border-white/10"}`}>
                      {v.active ? "active" : "off"}
                    </button></td>
                    <td className="py-3 text-right space-x-1">
                      <Link to={`/admin/vendors/${v.id}`} className="text-xs text-indigo-300 hover:underline" data-testid={`vendor-open-${v.id}`}>Open →</Link>
                      <button onClick={() => rotate(v.id, v.name)} data-testid={`vendor-rotate-${v.id}`} className="text-xs bg-amber-500/15 border border-amber-500/30 text-amber-300 rounded-md px-2 py-1 inline-flex items-center gap-1">
                        <RefreshCw className="w-3 h-3" /> Rotate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      </div>

      {open && <NewVendorModal onClose={() => setOpen(false)} onCreated={(created) => { setOpen(false); setIssued({ ...created, mode: "created" }); load(); }} />}
      {issued && <SecretsRevealedModal payload={issued} onClose={() => setIssued(null)} />}
    </Shell>
  );
}

function NewVendorModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", contact_email: "", postback_url: "", revenue_share_pct: 100, active: true });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const { data } = await api.post("/admin/vendors", { ...form, revenue_share_pct: Number(form.revenue_share_pct) });
      toast.success("Vendor created");
      onCreated(data);
    } catch (err) { toast.error("Failed", { description: formatApiError(err) }); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="new-vendor-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="glass-strong rounded-2xl w-full max-w-lg p-6 relative">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-mute hover:text-white" data-testid="new-vendor-close"><X className="w-4 h-4" /></button>
        <h3 className="font-display font-extrabold text-2xl mb-1">Onboard a vendor</h3>
        <p className="text-mute text-sm mb-4">A signed API key and webhook secret will be generated. Share them securely — they are shown only once.</p>
        <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">Vendor name</label>
        <input required data-testid="new-vendor-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 mb-3 text-sm" />
        <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">Contact email</label>
        <input type="email" data-testid="new-vendor-email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 mb-3 text-sm" />
        <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">Postback URL (webhook)</label>
        <input type="url" data-testid="new-vendor-postback" placeholder="https://vendor.example.com/SyncLine/postback" value={form.postback_url} onChange={(e) => setForm({ ...form, postback_url: e.target.value })} className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 mb-3 text-sm font-mono" />
        <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">Revenue share %</label>
        <input type="number" min="0" max="100" step="1" data-testid="new-vendor-revshare" value={form.revenue_share_pct} onChange={(e) => setForm({ ...form, revenue_share_pct: e.target.value })} className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 mb-5 text-sm" />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm bg-white/5 hover:bg-white/10 border border-white/10" data-testid="new-vendor-cancel">Cancel</button>
          <button type="submit" disabled={saving} data-testid="new-vendor-submit" className="px-4 py-2 rounded-md text-sm bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60">{saving ? "Creating…" : "Create vendor & keys"}</button>
        </div>
      </form>
    </div>
  );
}

function SecretsRevealedModal({ payload, onClose }) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="secrets-modal">
      <div onClick={(e) => e.stopPropagation()} className="glass-strong rounded-2xl w-full max-w-xl p-6 relative border-2 border-amber-500/30">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-mute hover:text-white" data-testid="secrets-close"><X className="w-4 h-4" /></button>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-amber-500/20 border border-amber-400/30 flex items-center justify-center">
            <Key className="w-5 h-5 text-amber-300" />
          </div>
          <div>
            <h3 className="font-display font-extrabold text-2xl leading-tight">{payload.mode === "rotated" ? "Key rotated" : "Credentials generated"}</h3>
            <p className="text-xs text-mute mt-1">Copy these now. They will not be shown again.</p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-mute mb-1">API key (X-API-Key header)</div>
            <CopyField value={payload.api_key} testId="secrets-api-key" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-mute mb-1">Webhook signing secret</div>
            <CopyField value={payload.webhook_secret} testId="secrets-webhook-secret" />
            <div className="text-[11px] text-mute mt-1">Verify inbound postbacks by computing <code className="font-mono">HMAC-SHA256(secret, body)</code> and comparing to the <code className="font-mono">X-SyncLine-Signature</code> header.</div>
          </div>
          <div className="flex items-center gap-3 pt-2 border-t border-white/10">
            <Link2 className="w-4 h-4 text-indigo-300" />
            <Link to="/docs/vendor" target="_blank" className="text-sm text-indigo-300 hover:underline" data-testid="secrets-view-docs">View integration docs →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
